import { inject, Injectable } from '@angular/core';
import { LifecycleState, ParticipantType, PurchaseRequestState } from '@vendasta/protos/sms/v2/enums';
import { listPhoneProvisioningInfo$ } from '@vendasta/protos/sms/v2/phone-provisioning';
import { getAccountInfo$ } from '@vendasta/protos/sms/v2/sms-service';
import type {
  GetAccountInfoResponse,
  ListPhoneProvisioningInfoResponse,
  PhoneProvisioningInfo,
} from '@vendasta/protos/sms/v2/types';
import { catchError, distinctUntilChanged, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { SmsInjectionToken } from '../../token';

@Injectable({
  providedIn: 'root',
})
export class PhoneProvisioningService {
  private readonly config = inject(SmsInjectionToken);

  private readonly ownerId$ = this.config.ownerId$.pipe(
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /**
   * Latest PhoneProvisioningInfo row for the current Account Group, or null.
   * Backend orders by created DESC, so page-size 1 returns the newest attempt.
   * No row (e.g. legacy accounts) falls back to GetAccountInfo.
   */
  public readonly phoneProvisioningInfo$: Observable<PhoneProvisioningInfo | null> = this.ownerId$.pipe(
    switchMap((accountGroupId) => {
      if (!accountGroupId) {
        return of(null);
      }
      return listPhoneProvisioningInfo$({ filter: { accountGroupId }, pagingOptions: { pageSize: 1 } }).pipe(
        map((response: ListPhoneProvisioningInfoResponse) => response.items[0] ?? null),
        switchMap((info) => (info ? of(info) : this.provisioningInfoFromAccount$(accountGroupId))),
        catchError((err) => {
          console.error('listPhoneProvisioningInfo error:', err);
          return of(null);
        }),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /**
   * No-record fallback: a phone number on the Twilio account => synthesise a provisioned/active
   * row; empty number => null (keeps the existing no-record behaviour).
   */
  private provisioningInfoFromAccount$(accountGroupId: string): Observable<PhoneProvisioningInfo | null> {
    return getAccountInfo$({
      sender: { internalId: accountGroupId, type: ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP },
    }).pipe(
      map((account: GetAccountInfoResponse) =>
        account.phoneNumber
          ? ({
              accountGroupId,
              phoneNumber: account.phoneNumber,
              purchaseRequestState: PurchaseRequestState.PURCHASE_REQUEST_STATE_PROVISIONED,
              lifecycleState: LifecycleState.LIFECYCLE_STATE_ACTIVE,
            } as PhoneProvisioningInfo)
          : null,
      ),
      catchError((err) => {
        console.error('getAccountInfo fallback error:', err);
        return of(null);
      }),
    );
  }
}
